import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Space, notification, Row, Col } from 'antd'; // Keep antd for notification, etc.
import ButtonGroup from '@mui/material/ButtonGroup';
import Button from '@mui/material/Button';

import DebouncedInput from '../../../components/Inputs/DebouncedInput';
import { DebounceProvider } from '../../../components/Inputs/DebounceContext';

import { createPortfolioPath, updatePortfolioPath } from '../../../api/pathApi';
import PathPortfolioManager from './PathPortfolioManager';
import PathQuestionManager from './PathQuestionManager';

const PathEditor = () => {
  const dispatch = useDispatch();
  const { pathId } = useParams();
  const isNewPath = !pathId || pathId === 'new';

  // Grab existing path details if editing an existing path
  const pathObject = useSelector((state) =>
    !isNewPath
      ? state.user.data?.path_details.find((path) => path.id === pathId)
      : null
  );

  // Local state
  const [title, setTitle] = useState(pathObject?.title ?? '');
  const [selectedPortfolioIds, setSelectedPortfolioIds] = useState(
    pathObject?.portfolio_ids ?? []
  );
  const [selectedQuestionIds, setSelectedQuestionIds] = useState(
    pathObject?.question_ids ?? []
  );

  // Which view is currently active
  const [activeView, setActiveView] = useState('portfolios');

  // Add/remove a portfolio ID
  const handleSelectPortfolioId = useCallback(
    (portfolioId, remove = false) => {
      setSelectedPortfolioIds((prev) => {
        if (remove) {
          return prev.filter((id) => id !== portfolioId);
        }
        if (prev.includes(portfolioId)) {
          return prev;
        }
        return [...prev, portfolioId];
      });
    },
    [pathId]
  );

  // Capture question IDs
  const handleSubmitQuestionIds = useCallback(
    (questionIds) => {
      setSelectedQuestionIds(questionIds);
    },
    [pathId]
  );

  // Notification helper
  const afterSave = useCallback(
    ({ status, title = '', text = '...' }) => {
      const notificationType = notification?.[status] || notification.info;
      notificationType({ message: title, description: text });
    },
    [pathId]
  );

  // Save or update path
  const handleSave = useCallback(() => {
    const formattedObject = {
      title,
      portfolio_ids: selectedPortfolioIds,
      question_ids: selectedQuestionIds
    };
    if (!title || title.length===0){
        notification.error({
            description: "Please enter a title for the path",
        });
        return
    }
    if (selectedPortfolioIds.length===0){
        notification.error({
            description: "Please select at least one portfolio"
        });
        return
    }
    if (selectedQuestionIds.length===0){
        notification.error({
            description: "Please select at least one question"
        });
        return
    }

    if (isNewPath) {
      dispatch(createPortfolioPath({ data: formattedObject }))
        .unwrap()
        .then((newPath) => {
          afterSave({
            status: 'success',
            title: 'Path created successfully',
            text: `Path ${newPath.title} created successfully`
          });
        })
        .catch((err) => {
          afterSave({
            status: 'error',
            title: 'Failed to create path',
            text: err.message
          });
        });
    } else {
      dispatch(updatePortfolioPath({ pathId, data: formattedObject }))
        .unwrap()
        .then((updatedPath) => {
          afterSave({
            status: 'success',
            title: 'Path updated successfully',
            text: `Path ${updatedPath.title} updated successfully`
          });
        })
        .catch((err) => {
          afterSave({
            status: 'error',
            title: 'Failed to update path',
            text: err.message
          });
        });
    }
  }, [
    dispatch,
    title,
    selectedPortfolioIds,
    selectedQuestionIds,
    isNewPath,
    pathId,
    afterSave
  ]);

  return (
    <div>
      {/* Title input and save button */}
        <Row justify={'center'} align={'middle'} gutter={32} className={'border-b border-gray-200 py-3'}>
            <Col>
                <DebouncedInput
                  onChange={setTitle}
                  value={title}
                  placeholder="Enter Path Title"
                  className="w-[300px]"
                />
                <Button className={'mx-3'} onClick={handleSave} size={'small'} variant="outlined">
                  Save
                </Button>
            </Col>
            <Col>
                <ButtonGroup >
                  <Button
                      size={'small'}
                    onClick={() => setActiveView('portfolios')}
                    variant={activeView === 'portfolios' ? 'contained' : 'outlined'}
                  >
                    Portfolios
                  </Button>
                  <Button
                      size={'small'}
                    onClick={() => setActiveView('questions')}
                    variant={activeView === 'questions' ? 'contained' : 'outlined'}
                  >
                    Questions
                  </Button>
              </ButtonGroup>
            </Col>
        </Row>
        {/* Conditionally render each view */}
      <div className="px-12 py-4">
        {activeView === 'portfolios' && (
          <PathPortfolioManager
            selectedIds={selectedPortfolioIds}
            onSubmit={handleSelectPortfolioId}
          />
        )}
        {activeView === 'questions' && (
          <PathQuestionManager
            selectedIds={selectedQuestionIds}
            onSubmit={handleSubmitQuestionIds}
          />
        )}
      </div>
    </div>
  );
};

export default PathEditor;
